import { RootState } from "@/redux/store";
import {
  useParkPaymentMutation,
  useGetDocumentTypeDropdown,
  useGetGlMasterDataDropdown,
  useGetLineItemsByVendorId,
  useGetPaymentMethodByCommunity,
  useGetPaymentTermsByCommunity,
  useGetVendorListByCommunityId,
  useGetParkedDocById,
  usePostDocMutation,
  useUpdateParkPaymentMutation,
  useGetPostedDocById,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useState } from "react";
import { toast } from "@/components/Toast";
import { UseApiProps } from "./types";

const useApi = ({ onBackClick, isEntryView }: UseApiProps) => {
  const { paymentId, action, paymentAction } = useParams();
  const isPosted = paymentAction == "post";
  const isParked = paymentAction == "park";
  const queryClient = useQueryClient();
  const [lineItemComment, setLineItemComment] = useState<string>("");
  const [lineItem, setLineItem] = useState<any>(null);
  const [openApproveRejectDialog, setOpenApproveRejectDialog] =
    useState<boolean>(false);
  const [openPostDialog, setOpenPostDialog] = useState<boolean>(false);
  const [vendorId, setVendorId] = useState<string>("");
  const isView = action == "view";
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: vendorList, isLoading: isVendorLoading } =
    useGetVendorListByCommunityId({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: paymentMethodOptions, isLoading: isPaymentMethodLoading } =
    useGetPaymentMethodByCommunity({communityId: communityDetail?.communityId});

  const { data: paymentTermOptions, isLoading: isPaymentTermLoading } =
    useGetPaymentTermsByCommunity({communityId: communityDetail?.communityId});

  const { data: lineItemsInfo, isLoading: isLineItemsLoading } =
    useGetLineItemsByVendorId({
      vendorId: vendorId as string,
      queryParams: {
        enabled: !!(vendorId && !isView),
      },
    });

  useLayoutEffect(() => {
    // queryClient.resetQueries(["vendor-payment", vendorId] as any);
  }, []);

  const { mutate: parkPaymentInfo, isPending: isPaymentPending } =
    useParkPaymentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: ({data}: any) => {
        if (data?.data?.errors) {
          const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
          toast.error(msg
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: ["payment-park-doc"],
          });
          onBackClick();
          toast.success(`Document (ID: ${data?.data?.parkDocumentHeaderId}) Added Successfully`);
        }
      },
    });

  const { mutate: updateParkPaymentInfo, isPending: isUpdatePaymentPending } =
    useUpdateParkPaymentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: ({data}: any) => {
        if (data?.data?.errors) {
          const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
          toast.error(msg
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: ["payment-park-doc"],
          });
          onBackClick();
          toast.success(`Document (ID: ${paymentId}) Updated Successfully`);
        }
      },
    });

  const { mutate: postPaymentInfo, isPending: isPaymentPostPending } =
    usePostDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["payment-post-doc"],
        });
        onBackClick();
        toast.success(`Document (ID: ${paymentId}) Posted Successfully`);
      },
    });
    
  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterDataDropdown({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: parkedPayment, isLoading: isLoadingParkPayment } =
    useGetParkedDocById({
      id: paymentId as string,
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!isParked,
      },
    });

  const { data: postPayment, isLoading: isLoadingPostPayment, refetch: refetchPostPayment } =
    useGetPostedDocById({
      id: paymentId as string,
      communityId: communityDetail?.communityId,
      entryView: isEntryView,
      queryParams: {
        enabled: !!isPosted,
      },
    });

  useEffect(() => {
    if (isPosted) {
      refetchPostPayment();
    }
  }, [isEntryView]);

  const paymentInvoiceData = isPosted ? postPayment : parkedPayment;
  const isLoadingPaymentInvoice = isPosted ? isLoadingPostPayment : isLoadingParkPayment;

  return {
    vendorList,
    isVendorLoading,
    documentTypes,
    isDocumentTypeLoading,
    lineItemComment,
    setLineItemComment,
    lineItem,
    setLineItem,
    openApproveRejectDialog,
    setOpenApproveRejectDialog,
    openPostDialog,
    setOpenPostDialog,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    lineItemsInfo,
    isLineItemsLoading,
    setVendorId,
    parkPaymentInfo,
    isPaymentPending,
    glData,
    isLoadingGlMaster,
    paymentInvoiceData,
    isLoadingPaymentInvoice,
    postPaymentInfo,
    isPaymentPostPending,
    updateParkPaymentInfo,
    isUpdatePaymentPending
  };
};

export default useApi;
